<template>
  <main-section
    :isEditMode="isEditMode"
    :isDiff="field.isDiff"
    :isOutdated="isOutdated"
    :itemType="'FIELD'"
    :typename="'field'"
    :item="field"
    :onUpdate="updateField"
    :onCreate="createField"
    :onDelete="deleteField"
    @change-chosen="onChangeChosen"
    @revert="onRevert"
  >
    <application-menu slot="top-menu" :app="app" :field="field" ref="applicationMenu" />

    <fieldset slot="fieldset" :disabled="isOutdated" id="fieldView">
      <!-- NAME -->
      <div
        class="field required"
        v-diff-field="fieldDiff.name"
        :class="{ error: $v.field.name.$error }"
      >
        <label for="fieldName">{{ $t('NAME') }}</label>
        <input
          id="fieldName"
          type="text"
          :placeholder="$t('NAME')"
          v-model.trim="field.name"
          :disabled="usedInFlows.length !== 0 || field.isDiff"
          @click="$v.field.name.$touch()"
        />
      </div>
      <modal
        :ctrl="confirmModalCtrl"
        :modalClasses="['mini']"
        :positiveLabel="{ text: $t('YES'), color: 'red' }"
        :negativeLabel="{ text: $t('NO'), color: 'green' }"
      />

      <!-- LOCATOR TAB HEADERS -->
      <div class="ui top attached tabular menu" id="tabHeaders">
        <router-link
          class="narrow item tab-header"
          v-for="(locator, idx) in vuelidateLocators"
          :key="idx"
          :class="{
            active:
              idx === currentTabIndex ||
              (idx === allLocators.length - 1 && currentTabIndex >= allLocators.length),
            error: !locator.noEmptyLocators
          }"
          :to="{
            name: routeName,
            params: { appId: appId, fieldId: fieldId, tabIndex: idx }
          }"
        >
          <i
            class="fitted link circle chevron left icon"
            @click.prevent="shiftLocator(locator.$model, -1)"
            :class="{ hidden: idx <= 0 || idx !== currentTabIndex }"
            v-show="idx > 0 && !field.isDiff"
          />

          <i
            class="fitted link exclamation icon changed-tab"
            v-if="
              field.isDiff && diffObjectHasChangesRec(getLocatorDiffForIndex(idx, fieldDiff), 3)
            "
          />

          <span
            style="margin: 0 0.45em"
            :data-tooltip="locator.noEmptyLocators ? null : $t('FIELD_ALTERNATIVE_EMPTY')"
          >
            {{ getTabHeaderText(idx, locator.$model) }}
          </span>

          <i
            class="fitted link red times circle icon"
            v-confirm-click="{
              modalController: confirmModalCtrl,
              onConfirm: deleteLocator,
              confirmMessage: $t('FIELD_ALTERNATIVE_DELETE_CONFIRM')
            }"
            :class="{ hidden: idx !== currentTabIndex }"
            v-show="allLocators.length > 1 && !field.isDiff"
          />
        </router-link>
        <div class="narrow item" v-show="!field.isDiff">
          <div class="ui mini green icon button" @click="addLocator">
            <i class="plus icon" />
          </div>
        </div>
      </div>
      <!-- LOCATOR CONTENT -->
      <div
        class="ui bottom attached segment"
        :class="{ tall: allLocators.length > 2, stacked: allLocators.length > 1 }"
      >
        <!-- LOCATOR TYPE (PATH/IMAGE) -->
        <div class="ui two column grid">
          <div class="row">
            <div class="sixteen wide column" ref="droptarget">
              <!-- FIELD PREVIEW IMAGE -->
              <div class="ui field">
                <div style="position: relative; display: inline-block;">
                  <img
                    class="ui disabled middle aligned bordered image"
                    v-if="currentLocator.image"
                    :src="'data:image/png;base64,' + currentLocator.image"
                    style="max-height: 100px; min-height: 50px;"
                  />
                  <div
                    v-if="currentLocator.image && !field.isDiff"
                    class="ui icon corner-delete button"
                    @click.prevent="resetTargetImage"
                  >
                    <i class="red delete icon" />
                  </div>
                </div>
                <div v-if="!currentLocator.image">
                  <i class="huge disabled icons">
                    <i class="large red dont icon" />
                    <i class="image outline icon" />
                  </i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Secondary tabs for path and image locator types -->
        <div class="ui top attached secondary tabular pointing menu">
          <a
            class="item"
            @click="currentLocator.type = 'path'"
            :class="{ active: currentLocator.type === 'path', disabled: field.isDiff }"
          >
            {{ $t('FIELD_PATH_IDENTIFICATION') }}
          </a>
          <a
            class="item"
            @click="currentLocator.type = 'image'"
            :class="{ active: currentLocator.type === 'image', disabled: field.isDiff }"
          >
            {{ $t('FIELD_OPTICAL_IDENTIFICATION') }}
          </a>
        </div>
        <div class="ui basic segment">
          <div class="ui two column grid">
            <!-- PATH LOCATOR -->
            <template v-if="currentLocator.type === 'path'">
              <div class="row">
                <div class="sixteen wide column">
                  <!-- PATH EDITOR -->
                  <div class="field" v-if="!isPathFinding" v-diff-field="currentLocatorDiff.path">
                    <div class="ui action input">
                      <path-editor
                        id="fieldPath"
                        v-show="usePathEditor"
                        :path="currentLocator.path"
                        :path-infos="pathInfos"
                        @change="currentLocator.path = $event"
                        :disabled="isPathFinding || field.isDiff"
                        :app="app"
                      />
                      <input
                        id="fieldPath"
                        class="monospaced"
                        type="text"
                        :placeholder="$t('PATH')"
                        v-show="!usePathEditor"
                        v-model="currentLocator.path"
                        :disabled="isMonitoring || isOutdated || field.isDiff"
                      />

                      <button
                        class="ui green icon button"
                        :class="{
                          disabled: !supportsFieldDiscovery
                        }"
                        type="button"
                        @click.prevent="toggleFieldDiscovery('path')"
                        :title="$t('FIELD_POINT_AND_CLICK')"
                        :disabled="field.isDiff"
                      >
                        <i class="crosshairs icon" />
                      </button>
                      <button
                        class="ui red icon button"
                        type="button"
                        @click.prevent="resetPath"
                        :disabled="isOutdated || !isPathInserted || field.isDiff"
                      >
                        <i class="recycle icon" />
                      </button>
                      <!-- Input settings button -->
                      <button
                        class="ui blue icon button"
                        type="button"
                        @click.prevent="togglePathSettings"
                        :title="$t('FIELD_PATH_SETTINGS')"
                        :disabled="field.isDiff"
                      >
                        <i class="cog icon" />
                      </button>
                    </div>
                    <div class="ui inline dropdown" ref="pathSettingsMenuContainer">
                      <div
                        ref="pathSettingsMenu"
                        class="menu transition"
                        :class="{ visible: showPathSettings, hidden: !showPathSettings }"
                        :style="[
                          showPathSettings ? 'display: block !important;' : 'display: none;'
                        ]"
                      >
                        <div class="item">
                          <div class="ui toggle checkbox">
                            <input id="usePathEditor" type="checkbox" v-model="usePathEditor" />
                            <label for="usePathEditor">
                              {{ $t('FIELD_USE_PATH_EDITOR') }}
                            </label>
                          </div>
                        </div>
                        <div class="item">
                          <tri-state-toggle
                            :owner="currentLocator"
                            prop-name="includeInvisible"
                            :label="$t('FIELD_LOCATOR_INCLUDE_INVISIBLE')"
                            v-diff-field="currentLocatorDiff.includeInvisible"
                            :disabled="isOutdated || field.isDiff"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="field" v-if="isPathFinding">
                    <div class="ui action input">
                      <input type="text" disabled="disabled" v-model="hoveredPath" id="fieldPath" />
                      <button
                        class="ui disabled double loading button"
                        type="button"
                        :class="{
                          green: monitorInitialized,
                          yellow: !monitorInitialized
                        }"
                      />
                      <button
                        class="ui red icon button"
                        type="button"
                        @click.prevent="toggleFieldDiscovery('path')"
                      >
                        <i class="stop circle icon" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </template>

            <!-- OPTICAL LOCATOR -->
            <template v-if="currentLocator.type === 'image'">
              <div class="row">
                <div class="column">
                  <div class="ui buttons">
                    <!-- POINT & CLICK -->
                    <div
                      class="ui left attached labeled icon button"
                      :class="{
                        green: !isMonitoring,
                        red: isImageFinding,
                        loading: isImageFinding,
                        disabled: !supportsFieldDiscovery || isOutdated || field.isDiff
                      }"
                      @click.prevent="toggleFieldDiscovery('image')"
                    >
                      {{ $t('FIELD_POINT_AND_CLICK') }}
                      <i class="crosshairs icon" />
                    </div>
                    <div class="or" :data-text="$t('OR_SHORT')"></div>
                    <!-- MANUAL FRAMING BUTTON -->
                    <div
                      class="ui right labeled icon button"
                      :class="{
                        green: !isMonitoring,
                        red: isScreenshotFinding,
                        loading: isScreenshotFinding,
                        disabled: isOutdated || field.isDiff
                      }"
                      @click.prevent="toggleScreenshotTaker"
                    >
                      {{ $t('GRAB_SCREENSHOT') }}
                      <i class="object group outline icon" />
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="column">
                  <div class="ui two column grid">
                    <div class="row">
                      <div class="column">
                        <div class="ui field" v-diff-field="diffOffsetX">
                          <label for="xoffset">{{ $t('X_OFFSET') }}</label>
                          <input
                            id="xoffset"
                            class="ui basic input"
                            type="number"
                            placeholder="0"
                            v-model.number="currentLocator.offset.x"
                            :disabled="isOutdated || !isScreenshotTaken || field.isDiff"
                          />
                        </div>
                      </div>

                      <div class="column">
                        <div class="ui field" v-diff-field="diffOffsetY">
                          <label for="yoffset">{{ $t('Y_OFFSET') }}</label>
                          <input
                            id="yoffset"
                            class="ui basic input"
                            type="number"
                            placeholder="0"
                            v-model.number="currentLocator.offset.y"
                            :disabled="isOutdated || !isScreenshotTaken || field.isDiff"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="column">
                        <div class="ui field" v-diff-field="currentLocatorDiff.matchconfidence">
                          <label for="matchconfidence">{{ $t('MATCH_CONFIDENCE') }}</label>
                          <input
                            id="matchconfidence"
                            class="ui basic input"
                            type="number"
                            step="0.1"
                            min="0"
                            max="1"
                            placeholder="0.9"
                            v-model.number="currentLocator.matchconfidence"
                            :disabled="isOutdated || !isScreenshotTaken || field.isDiff"
                          />
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="sixteen wide column">
                        <div class="field" v-diff-field="currentLocatorDiff.matchondesktop">
                          <div class="ui toggle checkbox">
                            <input
                              id="matchondesktop"
                              type="checkbox"
                              v-model="currentLocator.matchondesktop"
                              :disabled="isOutdated || !isScreenshotTaken || field.isDiff"
                            />
                            <label for="matchondesktop">
                              {{ $t('MATCH_ON_DESKTOP') }}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column" style="overflow-x: auto;">
                  <div class="ui compact basic segment">
                    <div style="position: relative;">
                      <image-offsetter
                        v-if="currentLocator.image"
                        :imageData="currentLocator.image"
                        :offsetX="currentLocator.offset.x"
                        :offsetY="currentLocator.offset.y"
                        @offsets="currentLocator.offset = $event"
                        :disabled="field.isDiff"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="ui top attached secondary tabular pointing menu">
          <div
            class="item"
            :class="{ active: fieldMenuShowing === 'test' }"
            @click="fieldMenuShowing = 'test'"
          >
            {{ $t('FIELD_TESTING') }}
          </div>
          <div
            class="item"
            :class="{ active: fieldMenuShowing === 'tree' }"
            @click="fieldMenuShowing = 'tree'"
          >
            {{ $t('FIELD_TREE') }}
          </div>
        </div>
        <div class="ui basic segment">
          <!-- TREE UI -->
          <template v-if="fieldMenuShowing === 'tree'">
            <!-- menu -->
            <div class="ui secondary menu">
              <!-- refresh -->
              <div class="narrow item">
                <div
                  class="ui green mini icon button"
                  :class="{ loading: flowRunning }"
                  @click="refreshTree"
                >
                  <i class="refresh icon" />
                </div>
              </div>

              <!-- search -->
              <div class="narrow item">
                <form class="ui mini action input" @submit.prevent="" :class="{ disabled: !tree }">
                  <input
                    type="text"
                    v-model="treeFilter"
                    :placeholder="$t('FIELD_SEARCH')"
                    style="width: auto"
                  />
                  <div class="ui mini icon button" @click.prevent="">
                    <i class="search icon" />
                  </div>
                </form>
              </div>

              <!-- right aligned menu -->
              <div class="right aligned menu">
                <!-- expand all -->
                <div class="narrow item">
                  <div class="ui mini button" :class="{ disabled: !tree }" @click="expandTree">
                    {{ $t('FIELD_TREE_EXPAND_ALL') }}
                  </div>
                </div>
              </div>
            </div>
            <div id="tree" v-if="tree">
              <!-- Tree -->
              <div class="ui basic segment">
                <TreeView
                  :node="node"
                  :filter="treeFilter"
                  v-for="node in tree"
                  :key="node.id || node.name"
                  :actions="[
                    { name: 'highlight', icon: 'eye', color: 'blue', text: $t('HIGHLIGHT') },
                    { name: 'click', icon: 'mouse', color: 'blue', text: $t('ACTION_CLICK') },
                    { name: 'use', icon: 'up arrow', text: $t('USE_AS_FIELD_PATH') }
                  ]"
                  @action="handleNodeAction"
                  @select="selectNode"
                />
              </div>

              <!-- Details -->
              <div class="details">
                <hr class="vertically" />
                <Properties :of="selectedNode && selectedNode.props" :ignore="['children']" />
              </div>
            </div>
            <div
              v-else
              class="ui visible warning message"
              v-html="$t('FIELD_TREE_NONE_HTML')"
            ></div>
          </template>

          <!-- FIELD TEST UI -->
          <div v-show="allLocators.length > 0 || isOutdated" v-if="fieldMenuShowing === 'test'">
            <div class="ui top attached inverted black menu" id="locatorTestMenu">
              <div class="narrow item">
                <button
                  class="ui blue icon button"
                  :class="{ loading: isHighlighting }"
                  @click.prevent="highlightField()"
                  :disabled="isOutdated"
                >
                  <i class="eye icon" />
                </button>
              </div>

              <!-- Disabled until we can remove the old non-flow based highlight socket code (all manatees support ad-hoc flows) <a
              class="item"
              @click.prevent="
                runAction('(field.highlight(), Wait.forSeconds(1), field.lowlight())')
              "
              :class="{ disabled: fieldTestPending }"
            >
              highlight
            </a> -->
              <a
                class="item"
                @click.prevent="runAction('field.inspect()')"
                :class="{ disabled: fieldTestPending }"
              >
                .inspect()
              </a>
              <a
                class="item"
                @click.prevent="runAction('field.read()')"
                :class="{ disabled: fieldTestPending }"
              >
                .read()
              </a>
              <div class="ui simple dropdown item" :class="{ disabled: fieldTestPending }">
                {{ $t('FIELD_TEST_CLICK') }}
                <i class="dropdown icon" />
                <div class="inverted menu">
                  <a class="item" @click.prevent="runAction('field.click()')">.click()</a>
                  <a class="item" @click.prevent="runAction('field.rightClick()')">
                    .rightClick()
                  </a>
                  <a class="item" @click.prevent="runAction('field.doubleClick()')">
                    .doubleClick()
                  </a>
                  <a class="item" @click.prevent="runAction('field.simulateClick()')">
                    .simulateClick()
                  </a>
                </div>
              </div>
              <div class="ui simple dropdown item" :class="{ disabled: fieldTestPending }">
                {{ $t('FIELD_TEST_MISC') }}
                <i class="dropdown icon" />
                <div class="inverted menu">
                  <a class="item" @click.prevent="runAction('field.input(\'manatee\')')">
                    .input('manatee')
                  </a>
                  <a class="item" @click.prevent="runAction('field.inputNative(\'manatee\')')">
                    .inputNative('manatee')
                  </a>
                  <a class="item" @click.prevent="runAction('field.inputNativeAsync(\'manatee\')')">
                    .inputNativeAsync('manatee')
                  </a>
                  <a
                    class="item"
                    @click.prevent="runAction('field.inputNativeWithDelay(\'manatee\', 300)')"
                  >
                    .inputNativeWithDelay('manatee', 300)
                  </a>
                  <a class="item" @click.prevent="runAction('Window.sendKeys(\'manatee\')')">
                    Window.sendKeys('manatee')
                  </a>
                  <a class="item" @click.prevent="runAction('field.focus()')">
                    .focus()
                  </a>
                  <a class="item" @click.prevent="runAction('field.exists()')">
                    .exists()
                  </a>
                  <a class="item" @click.prevent="runAction('field.bounds()')">
                    .bounds()
                  </a>
                  <a class="item" @click.prevent="runAction('field.check()')">
                    .check()
                  </a>
                  <a class="item" @click.prevent="runAction('field.uncheck()')">
                    .uncheck()
                  </a>
                  <a class="item" @click.prevent="runAction('field.isChecked()')">
                    .isChecked()
                  </a>
                </div>
              </div>
              <a
                class="icon item delayed-visibility"
                :class="{ 'make-visible': fieldTestPending, hidden: !fieldTestPending }"
                @click.prevent="cancelAction()"
              >
                <i class="red cancel icon" />
              </a>
              <div class="right narrow item grow-on-hover">
                <form class="ui action input" @submit.prevent="runAction(customActionCode)">
                  <input
                    type="text"
                    :placeholder="'field.focus(), field.read()'"
                    v-model="customActionCode"
                    style="width: auto"
                  />
                  <div class="ui button" @click.prevent="runAction(customActionCode)">
                    {{ $t('FIELD_TEST_RUN') }}
                  </div>
                </form>
              </div>
            </div>
            <!-- Output -->
            <div
              class="ui bottom attached segment inverted"
              style="display: flex; min-height: 150px"
            >
              <div class="h-scrolling-output">
                <pre
                  class="ui field monospaced"
                  :style="{ height: outputHeight || 'auto' }"
                  ref="outputArea"
                  >{{ testOutput }}</pre
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- FLOW LIST -->
      <div class="field" v-if="usedInFlows.length > 0 && isEditMode">
        <label for="flowList">
          {{ $t('USED_IN', { thing: $tc('FLOW', usedInFlows.length) }) }}
        </label>
        <div class="ui link list" id="flowList">
          <router-link
            class="item"
            :to="{ name: 'app-flow', params: { appId: app.id, flowId: f.flow.id, mode: 'auto' } }"
            :key="f.flow.id"
            v-for="f in usedInFlows"
          >
            <span>
              <i class="file code outline icon" v-bind:class="{ hidden: isFieldIconHidden(f) }" />
              <i class="anchor icon" v-bind:class="{ hidden: isAnchorIconHidden(f) }" />
              <span class="content">{{ f.flow.name }}</span>
            </span>
          </router-link>
        </div>
      </div>
      <div v-if="isEditMode && usedInFlows.length === 0">
        <span class="text bold">{{ $t('FIELD_NOT_USED') }}</span>
      </div>
    </fieldset>

    <loading-button
      slot="rightBottomMenuSlot2"
      class="ui left labeled icon button"
      :onClick="() => copyField(field.id)"
      :disabled="isOutdated || field.isDiff"
    >
      {{ $t('COPY', { thing: '' }) }}
      <i class="copy icon" />
    </loading-button>
  </main-section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../../scss/variables';

#tree {
  max-height: 600px;
  overflow-y: auto;

  .details {
    position: sticky;
    top: 10px;
  }
}

a.disabled {
  opacity: 0.45 !important;
}

.text.bold {
  font-weight: bold;
}

.hidden {
  visibility: hidden;
}

#fieldView {
  &:focus {
    outline: none;
  }

  // For things that shouldn't appear straight away
  .delayed-visibility {
    opacity: 0;
    transition: opacity 50ms ease-in;
    transition-delay: 500ms;

    &.make-visible {
      opacity: 1;
    }
  }

  .button {
    &.corner-delete {
      position: absolute;
      right: 0px;
      top: 0px;
      padding: 1px;
      margin: 0;
    }
  }

  .item {
    &.narrow {
      padding-left: 8px;
      padding-right: 8px;
    }

    &.grow-on-hover {
      transition: flex 300ms ease-in;

      &:hover,
      &:focus-within {
        flex-grow: 1;
      }
    }
  }

  .h-scrolling-output {
    flex-grow: 0;
    flex-basis: 100%;
    width: 200px;
    overflow-x: auto;
  }

  // Bump the specificity to overrule fomantic styles
  #tabHeaders {
    .changed-tab {
      color: rgb(red($changeBgColor), green($changeBgColor), blue($changeBgColor));
    }
    .tab-header.error {
      color: #9f3a38;
      background-color: #fff6f6;
    }
  }
}

.ui.toggle.button.active.path-editor-toggle {
  background-color: $blue;
}

#tree {
  display: flex;
  justify-content: space-between;
  flex-flow: row nowrap;

  .details {
    display: flex;
    hr {
      margin: 10px 20px 10px 20px;
      display: inline-block;
    }
  }
}

hr,
hr.vertically {
  background-color: #d4d4d5;
  border: none;
}

hr {
  width: 100%;
  height: 1px;
}

hr.vertically {
  width: 1px;
  height: 100%;
}
</style>

<script>
// @flow
import _ from 'lodash'
import {
  Application,
  Field,
  Flow,
  MonitorSocket,
  Toast,
  ErrorCodes,
  UnsavedChecker
} from 'services'
import ApplicationMenu from './../ApplicationMenu'
import {
  MainSection,
  LoadingButton,
  ImageOffsetter,
  Modal,
  ModalController,
  TriStateToggle,
  TreeView
} from 'ui'
import { mapActions } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import PathEditor from './PathEditor'
import Properties from './Properties'

function noEmptyLocators(locator) {
  return (locator.type === 'path' && locator.path) || (locator.type === 'image' && locator.image)
}

const field = {
  path: '',
  pathInfos: [],
  name: '',
  variablename: '',
  actions: [],
  image: '',
  offset: {},
  type: 'path'
}

export default {
  name: 'field',
  mixins: [UnsavedChecker.mixin],
  components: {
    TriStateToggle,
    ApplicationMenu,
    LoadingButton,
    MainSection,
    PathEditor,
    ImageOffsetter,
    Modal,
    TreeView,
    Properties
  },

  props: {
    appId: String,
    fieldId: String,
    urlTabIndex: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      mode: '',
      app: {},
      field: { ...field },
      ms: null,
      monitorBy: null,
      monitorFor: null,
      usedInFlows: [],
      isOutdated: false,
      fieldDiff: {},
      pathInfos: [],
      usePathEditor: true,
      allLocators: [],
      hoveredPath: '',
      testOutput: '',
      flowStartMs: 0,
      customActionCode: '',
      outputHeight: 20,
      currentDebugSocket: null,
      waitingForSocket: false,
      flowRunning: false,
      debouncedInitializeMessage: null,
      monitorInitialized: false,
      confirmModalCtrl: new ModalController(),
      showPathSettings: false,
      fieldMenuShowing: 'test',
      selectedNode: null,
      treeFilter: '',
      finishedCallback: null,
      tree: null
    }
  },

  validations: {
    field: {
      name: {
        required
      }
    },
    allLocators: {
      $each: {
        noEmptyLocators
      }
    }
  },

  computed: {
    vuelidateLocators() {
      const iter = this.$v.allLocators.$each.$iter
      return _.keys(iter)
        .map(si => parseInt(si, 10))
        .sort()
        .map(idx => iter[idx])
    },
    diffOffsetX() {
      return this.currentLocatorDiff.offset && this.currentLocatorDiff.offset.x
    },
    diffOffsetY() {
      return this.currentLocatorDiff.offset && this.currentLocatorDiff.offset.y
    },
    fieldTestPending() {
      return this.waitingForSocket || !!this.flowStartMs
    },

    currentLocator() {
      return this.allLocators[this.currentTabIndex] || {}
    },

    currentLocatorDiff() {
      return this.getLocatorDiffForIndex(this.currentTabIndex, this.fieldDiff)
    },

    isMonitoring() {
      return this.ms && this.monitorBy !== null
    },

    isPathFinding() {
      return this.isMonitoring && this.monitorBy === 'byfield' && this.monitorFor === 'path'
    },

    isImageFinding() {
      return this.isMonitoring && this.monitorBy === 'byfield' && this.monitorFor === 'image'
    },

    isScreenshotFinding() {
      return this.isMonitoring && this.monitorBy === 'byscreenshot' && this.monitorFor === 'image'
    },

    currentTabIndex() {
      if (this.urlTabIndex >= this.allLocators.length) return this.allLocators.length - 1
      if (this.urlTabIndex < 0) return 0
      return this.urlTabIndex
    },

    isHighlighting() {
      return this.ms && this.monitorBy === 'highlight'
    },

    supportsFieldDiscovery() {
      return this.app && this.app.type
    },
    isEditMode() {
      return this.mode === 'edit'
    },
    isPathInserted() {
      return !_.isEmpty(this.currentLocator.path)
    },
    isScreenshotTaken() {
      return !_.isEmpty(this.currentLocator.image)
    },
    fieldCreationCode() {
      const includeInvisibleProp =
        this.currentLocator.includeInvisible == null
          ? ''
          : `includeInvisible: ${this.currentLocator.includeInvisible},`
      const traversalArgsProp =
        this.currentLocator.traversalArgs == null
          ? ''
          : `traversalArgs: ${JSON.stringify(this.currentLocator.traversalArgs)},`
      return this.currentLocator.type === 'path'
        ? `new Field({ path: '${this.currentLocator.path
            .replace(/\\/g, '\\\\')
            .replace(
              /'/g,
              "\\'"
            )}', ${includeInvisibleProp}${traversalArgsProp}name: '${this.field.name.replace(
            /'/g,
            "\\'"
          )}' })`
        : `new Field({ image: '${this.currentLocator.image.replace(/'/g, "\\'")}', offset: { x: ${
            this.currentLocator.offset.x
          }, y: ${this.currentLocator.offset.y}}, matchConfidence: ${
            this.currentLocator.matchconfidence
          }, matchOnDesktop: ${!!this.currentLocator
            .matchondesktop}, name: '${this.field.name.replace(/'/g, "\\'")}' })`
    },
    routeName() {
      return this.$route.name
    }
  },

  methods: {
    ...mapActions('debugger', ['runFlow', 'closeDebugSocket', 'getOpenDebugSocket']),

    handleNodeAction(action, node) {
      switch (action.name) {
        case 'highlight':
          this.runHighlight(node.path)
          break
        case 'use':
          this.runUse(node.path)
          break
        case 'click':
          this.runClick(node.path)
          break
      }
    },

    buildTree(n, path, nameSelectors, detailSelectors, childrenSelector) {
      return {
        props: n,
        path,
        id: `${Math.floor(Math.random() * 1000000)}`,
        name: n[_.find(nameSelectors, selector => n[selector])] ?? '?',
        detail: n[_.find(detailSelectors, selector => n[selector])] ?? '?',
        children: _.map(n[childrenSelector], (child, index) =>
          this.buildTree(
            child,
            `${path}/*#${index}`,
            nameSelectors,
            detailSelectors,
            childrenSelector
          )
        )
      }
    },

    handleInspectTree(message) {
      if (message && message.result && message.result.result && message.result.result.Value) {
        this.tree = [
          this.buildTree(
            JSON.parse(message.result.result.Value),
            '*',
            ['accessibleName', 'name', 'simpleType', 'id', 'type'],
            ['simpleType', 'className', 'controlType', 'text', '_class'],
            'children'
          )
        ]
      }
    },

    handleUsePath(msg) {
      if (msg && msg.result && msg.result.result && msg.result.result.Value) {
        const resolved = JSON.parse(msg.result.result.Value)
        this.currentLocator.path = resolved.Path
        this.usePathEditor = !this.doesPathLookLikeCssSelector(resolved.Path)
        this.pathInfos = resolved.PathInfo
      }
    },

    runHighlight(path) {
      this.runOnDebugSocket(
        `(function(){ Window.focus(); new Field("${path}").highlight({color: 'blue'}); })();`
      )
    },

    runUse(path) {
      this.runOnDebugSocket(
        `(function(){ return JSON.stringify(new Field("${path}").resolve()); })();`,
        this.handleUsePath
      )
    },

    runClick(path) {
      this.runOnDebugSocket(`(function(){ new Field("${path}").click(); })();`)
    },

    runOnDebugSocket(code, callback) {
      this.waitingForSocket = true
      this.finishedCallback = callback
        ? function(msg) {
            callback(msg)
            this.finishedCallback = null
          }
        : this.finishedCallback
      this.ensureDebugSocketOpen()
        .then(() => {
          this.waitingForSocket = false
          this.flowStartMs = Date.now()
          const flowCode = this.runFlow({
            flow: {
              id: 'dyn-run-on-debug-socket',
              code,
              application: this.appId
            },
            inputs: [],
            noTracerContext: true
          })
        })
        .catch(e => {
          this.waitingForSocket = false
          this.flowRunning = false
          console.warn('Failed to refresh tree.', e.message)
        })
    },

    refreshTree() {
      this.runOnDebugSocket(
        `JSON.stringify((function(field) { return new Field('*').inspect(); })());`,
        this.handleInspectTree
      )
    },

    expandNode(n) {
      this.$set(n, 'expanded', true)
      _.each(n.children, this.expandNode)
    },
    expandTree() {
      _.each(this.tree, this.expandNode)
    },

    selectNode(n) {
      this.selectedNode = n
    },

    resetPath() {
      this.currentLocator.path = ''
    },

    togglePathSettings(evt) {
      console.debug('Toggling path settings', evt)
      this.showPathSettings = !this.showPathSettings

      if (!this.showPathSettings) return

      // This needs a setTimeout since the width etc are not right immediately
      setTimeout(() => {
        // place menu below the evt target
        const menuContainer = this.$refs.pathSettingsMenuContainer
        const menu = this.$refs.pathSettingsMenu
        const target = evt.target
        menuContainer.style.position = 'absolute'
        menuContainer.style.left =
          target.offsetLeft + target.offsetWidth - menu.offsetWidth + 10 + 'px'
        menuContainer.style.top = target.offsetTop + target.offsetHeight + 5 + 'px'
      }, 0)
    },

    diffObjectHasChangesRec(o, depth) {
      if (typeof o !== 'object' || o == null) return false
      if (o.hasChange) return true
      return depth <= 0
        ? false
        : Object.getOwnPropertyNames(o).find(
            c => c != null && this.diffObjectHasChangesRec(o[c], depth - 1)
          )
    },

    resetTargetImage() {
      this.currentLocator.offset = {}
      this.currentLocator.image = ''
      this.currentLocator.matchondesktop = false
    },

    setOffset(evt) {
      this.currentLocator.offset = { x: evt.offsetX, y: evt.offsetY }
    },

    toggleScreenshotTaker() {
      if (this.ms) this.cleanupMonitorSession()
      else this.startFieldDiscovery('byscreenshot', 'image')
    },

    highlightField() {
      this.ms = MonitorSocket()
      this.ms.on('open', () => {
        this.monitorBy = 'highlight'
        this.ms.highlight(
          this.app,
          this.field.id,
          this.currentLocator.path || '',
          this.currentLocator.image || '',
          this.currentLocator.matchconfidence,
          this.currentLocator.offset.x || 0,
          this.currentLocator.offset.y || 0,
          this.currentLocator.matchondesktop || 0,
          this.currentLocator.traversalArgs || []
        )
      })
      this.ms.on('message', m => {
        if (m && m.Error) {
          this.testOutput = `Highlight failed: ${m.Reason || m.Error}`
        } else if (m && m.Message === 'OK') {
          this.testOutput = `Highlight successful with '${this.currentLocator.path ||
            (this.currentLocator.image ? 'image' : 'unknown')}'`
        }
        this.cleanupMonitorSession()
      })
      this.handleMonitorErrors()
    },

    toggleFieldDiscovery(pathOrImage) {
      if (!this.supportsFieldDiscovery) return
      if (this.ms) this.cleanupMonitorSession()
      else this.startFieldDiscovery('byfield', pathOrImage)
    },

    doesPathLookLikeCssSelector(path) {
      // Non empty path that contains no slashes might be css
      return (path && path.indexOf(' > ') >= 0) || path.indexOf('/') === -1
    },

    handleMonitorErrors() {
      this.ms.on('close', () => this.cleanupMonitorSession())
      this.ms.on('errormessage', e => {
        this.cleanupMonitorSession()
        if (e.ErrorCode === 2) {
          console.warn('Field finder failed due to app not being attached')
          Toast.warning(this.$i18n.t('MISSING_APPLICATION'), {
            remove: 2000,
            text: this.$i18n.t('MISSING_APPLICATION_MESSAGE')
          })
        } else if (e.Error) {
          console.warn('Field finder fail', e.Error)
          Toast.warning(this.$i18n.t('FIELD_FINDER_FAIL'), {
            remove: 2000,
            text: this.$i18n.t('FIELD_FINDER_FAIL_INIT_MSG', { cause: e.Error })
          })
        }
      })
    },

    startFieldDiscovery(mode, pathOrImage) {
      this.ms = MonitorSocket()
      this.monitorInitialized = false
      this.hoveredPath = ''
      this.ms.on('open', () => {
        this.ms.startMonitor(this.app, mode)
        this.monitorBy = mode
        this.monitorFor = pathOrImage
      })
      this.ms.on('selected', fc => {
        if (pathOrImage === 'path' || mode === 'byfield') {
          this.currentLocator.path = fc.Path
          this.currentLocator.image = fc.Image
          // If an older manatee is field finding, it won't report any traversal args
          this.currentLocator.traversalArgs = fc.TraversalArgs || []
          this.usePathEditor = !this.doesPathLookLikeCssSelector(fc.Path)
          this.pathInfos = fc.PathInfo
          this.cleanupMonitorSession()
        }
        if (pathOrImage === 'image') {
          this.currentLocator.image = fc.Image
          this.currentLocator.path = ''
        }
      })
      this.ms.on('hover', fc => {
        this.hoveredPath = fc.Path
      })
      this.ms.on('offsetselected', os => {
        this.currentLocator.offset = { x: os.X, y: os.Y }
        this.cleanupMonitorSession()
      })

      this.ms.on('initialized', () => (this.monitorInitialized = true))

      this.handleMonitorErrors()
    },

    cleanupMonitorSession() {
      if (this.ms) {
        this.ms.remove()
        this.ms = null
      }
      this.monitorBy = null
      this.monitorFor = null
      this.monitorInitialized = false
      this.hoveredPath = ''
    },

    readApp() {
      return Application.readApp(this.appId).then(app => {
        this.app = app
        return app
      })
    },

    readUsedIn() {
      return Flow.readFlowsForAppThatUseField(this.appId, this.fieldId).then(flows => {
        this.usedInFlows = _.orderBy(
          this.determineUsedAsPropertyForEachFlow(flows),
          ['flow.name'],
          ['asc']
        )
        return flows
      })
    },

    determineUsedAsPropertyForEachFlow(flows) {
      let usedFlows = []
      flows.forEach(flow => {
        if (this.countInArray(flow.usesfields, this.fieldId) > 1) {
          usedFlows.push({ usedAs: 'BOTH', flow: flow })
        } else if (flow.anchors && flow.anchors.includes(this.fieldId)) {
          usedFlows.push({ usedAs: 'ANCHOR', flow: flow })
        } else {
          usedFlows.push({ usedAs: 'FIELD', flow: flow })
        }
      })
      return usedFlows
    },

    countInArray(array, what) {
      return array.filter(item => item === what).length
    },

    isFieldIconHidden(flow) {
      return flow.usedAs !== 'FIELD' && flow.usedAs !== 'BOTH'
    },

    isAnchorIconHidden(flow) {
      return flow.usedAs !== 'ANCHOR' && flow.usedAs !== 'BOTH'
    },

    createField() {
      if (!this.$v.$invalid) {
        const appId = this.$route.params.appId

        return Field.readFieldsForApp(appId)
          .catch(err => {
            if (err.response.status === 404) {
              console.error(err)
              return []
            }
            throw err
          })
          .then(fields => {
            // Check if the field name is unique for this app.
            const fieldIndex = fields.findIndex(f => f.name === this.field.name)

            if (fieldIndex !== -1) {
              const constraintText = this.$i18n.t('UNIQUE_CONSTRAINT_EX', {
                thing: this.$i18n.tc('FIELD', 1).toLowerCase(),
                verb: this.$i18n.t('NAMED').toLowerCase(),
                subject: `'${this.field.name}'`
              })

              Toast.warning(
                this.$i18n.t('UNIQUE_CONSTRAINT', { thing: this.$i18n.tc('FIELD', 1) }),
                {
                  remove: 10000,
                  text: constraintText
                }
              )
              throw new Error(constraintText)
            } else {
              return Field.createField({
                ...this.field,
                application: appId
              })
            }
          })
          .then(res => {
            Toast.success(this.$i18n.t('CREATED', { thing: this.$i18n.tc('FIELD', 1) }), {
              remove: 5000
            })
            this.$router.push({
              name: 'app-field',
              params: { appId: appId, fieldId: res.id }
            })
          })
          .catch(err => {
            Toast.error(
              this.$i18n.t('ERR', {
                action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('FIELD', 1) })
              })
            )
            console.error(err)
          })
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('CREATE', { thing: this.$i18n.tc('FIELD', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    getFieldType(field): 'image' | 'path' {
      return field.type || (field.path ? 'path' : field.image ? 'image' : 'path')
    },

    setLocatorsFromRawField(field) {
      this.field = {
        offset: {},
        image: '',
        alternatives: [],
        type: this.getFieldType(field),
        ...field
      }
      this.$set(this, 'allLocators', this.getLocatorsFromField(field))
    },

    readField() {
      return Field.readField(this.fieldId)
        .then(field => {
          this.setLocatorsFromRawField(field)
          this.usePathEditor =
            this.app.type !== 'WEB-IE' && !this.doesPathLookLikeCssSelector(field.path)
          this.pathInfos = field.pathInfos
          this.isOutdated = false
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('READ', { thing: this.$i18n.tc('FIELD', 1) })
            })
          )
          console.error('Error loading field', err)
        })
    },

    getLocatorsFromField(field) {
      const primaryLocator = {
        path: field.path,
        image: field.image,
        offset: field.offset || {},
        type: this.getFieldType(field),
        matchondesktop: field.matchondesktop
      }
      if (field.matchconfidence != null) primaryLocator.matchconfidence = field.matchconfidence
      if (field.includeInvisible != null) primaryLocator.includeInvisible = field.includeInvisible
      if (field.traversalArgs != null) primaryLocator.traversalArgs = field.traversalArgs
      if (!_.isArray(field.alternatives)) return [primaryLocator]
      return [primaryLocator].concat(_.cloneDeep(field.alternatives))
    },

    cleanupField() {
      // Make sure that primary and alternatives of type 'image' have no path
      if (this.field.type === 'image') {
        this.field.path = ''
      }
      _.filter(this.field.alternatives, alt => alt.type === 'image').forEach(alt => (alt.path = ''))

      // Ensure the locators are updated to reflect cleaned up field
      this.$set(this, 'allLocators', this.getLocatorsFromField(this.field))
    },

    updateField() {
      if (!this.$v.$invalid) {
        if (!this.isOutdated) {
          this.cleanupField()

          return Field.readFieldsForApp(this.appId)
            .catch(err => {
              if (err.response.status === 404) {
                console.error(err)
                return []
              }
              throw err
            })
            .then(fields => {
              // Check if the field name is unique for this app.
              const fieldIndex = fields.findIndex(
                f => f.name === this.field.name && f.id !== this.field.id
              )

              if (fieldIndex !== -1) {
                const constraintText = this.$i18n.t('UNIQUE_CONSTRAINT_EX', {
                  thing: this.$i18n.tc('FIELD', 1).toLowerCase(),
                  verb: this.$i18n.t('NAMED').toLowerCase(),
                  subject: `'${this.field.name}'`
                })
                Toast.warning(
                  this.$i18n.t('UNIQUE_CONSTRAINT', { thing: this.$i18n.tc('FIELD', 1) }),
                  {
                    remove: 10000,
                    text: constraintText
                  }
                )
                throw new Error(constraintText)
              } else {
                return Field.updateField(this.field.id, this.field)
              }
            })
            .then(res => {
              this.field.version = res.version
              Toast.success(this.$i18n.t('UPDATED', { thing: this.$i18n.tc('FIELD', 1) }))
            })
            .catch(err => {
              if (err.code === ErrorCodes.ITEM_OUTDATED) {
                this.isOutdated = true
                Toast.error(
                  this.$i18n.t('ERR_OUTDATED_VERSION', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('FIELD', 1) }),
                    thing: this.$i18n.tc('FIELD', 1)
                  })
                )
              } else {
                Toast.error(
                  this.$i18n.t('ERR', {
                    action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('FIELD', 1) })
                  })
                )
                console.error(err)
              }
              throw err
            })
        } else {
          Toast.error(
            this.$i18n.t('ERR_OUTDATED_VERSION', {
              action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('FIELD', 1) }),
              thing: this.$i18n.tc('FIELD', 1)
            })
          )
          return Promise.reject('Outdated version')
        }
      } else {
        this.$v.$touch()
        Toast.warning(
          this.$i18n.t('WARN_REQUIRED', {
            action: this.$i18n.t('UPDATE', { thing: this.$i18n.tc('FIELD', 1) }),
            thing: this.$i18n.t('ALL_REQUIRED_FIELDS')
          })
        )
        return Promise.reject('Required fields missing')
      }
    },

    deleteField() {
      return Flow.readFlowsForAppThatUseField(this.$route.params.appId, this.field.id)
        .catch(err => {
          if (err.response.status === 404) {
            console.error(err)
            return []
          }
          throw err
        })
        .then(usedFlows => {
          if (usedFlows.length) {
            const constraintText = this.$i18n.t('USED_CONSTRAINT_EX', {
              action: this.$i18n
                .t('DELETE', {
                  thing: this.$i18n.tc('FIELD', 1)
                })
                .toLowerCase(),
              list: usedFlows.map((f, i) => ` ${f.name}${i < usedFlows.length ? '' : ', '}`)
            })
            Toast.warning(this.$i18n.t('USED_CONSTRAINT', { thing: this.field.name }), {
              remove: 10000,
              test: constraintText
            })
            throw new Error(constraintText)
          } else {
            return Field.deleteField(this.field.id)
          }
        })
        .then(() => {
          Toast.success(this.$i18n.t('DELETED', { thing: this.$i18n.tc('FIELD', 1) }))

          this.$router.push({
            name: 'app-settings',
            params: { appId: this.$route.params.appId }
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('DELETE', { thing: this.$i18n.tc('FIELD', 1) })
            })
          )
          console.error(err)
        })
    },

    copyField() {
      const appId = this.$route.params.appId

      return Field.readFieldsForApp(appId)
        .catch(err => {
          if (err.response.status === 404) {
            console.error(err)
            return []
          }
          throw err
        })
        .then(fields => {
          // Check if the field name is unique for this app.
          const fieldSuffix = ` ${this.$i18n.t('COPY', { thing: '' }).replace(' ', '')}`
          const fieldIndex = fields.findIndex(f => f.name === this.field.name + fieldSuffix)

          if (fieldIndex !== -1) {
            const constraintText = this.$i18n.t('UNIQUE_CONSTRAINT_EX', {
              thing: this.$i18n.tc('FIELD', 1).toLowerCase(),
              verb: this.$i18n.t('NAMED').toLowerCase(),
              subject: `'${this.field.name}${fieldSuffix}'`
            })

            Toast.warning(this.$i18n.t('USED_CONSTRAINT', { thing: this.field.name }), {
              remove: 10000,
              test: constraintText
            })
            throw new Error(constraintText)
          } else {
            return Field.copyField(this.field.id, fieldSuffix)
          }
        })
        .then(res => {
          Toast.success(this.$i18n.t('COPIED', { thing: this.$i18n.tc('FIELD', 1) }))
          this.$router.push({
            name: this.routeName,
            params: { appId: this.$route.params.appId, fieldId: res.id }
          })
        })
        .catch(err => {
          Toast.error(
            this.$i18n.t('ERR', {
              action: this.$i18n.t('COPY', { thing: this.$i18n.tc('FIELD', 1) })
            })
          )
          console.error(err)
        })
    },

    processFile(file) {
      if (file.size > 200000) {
        Toast.error(this.$i18n.t('ERR_FILE_TOO_LARGE', { max: '200kB' }))
        return
      }
      let r = new FileReader()
      r.onload = evt => {
        this.currentLocator.image = r.result.slice(_.indexOf(r.result, ',') + 1)
      }
      r.readAsDataURL(file)
    },
    editMode() {
      this.mode = 'edit'
      this.readApp()
      this.readField()
      this.readUsedIn()
    },

    createMode() {
      this.mode = 'create'
      this.readApp()
      this.field = { ...field }
      this.allLocators = []
      this.addLocator()
      this.usedInFlows = []
    },
    onChangeChosen({ changedItem, fullDiff }) {
      this.setLocatorsFromRawField(changedItem)
      this.fieldDiff = fullDiff
    },
    onRevert(field) {
      this.field = field
      this.fieldDiff = {}
    },
    getLocatorDiffForIndex(index, fieldDiff) {
      if (_.isEmpty(fieldDiff)) return {}
      if (index === 0)
        return {
          image: fieldDiff.image,
          path: fieldDiff.path,
          includeInvisible: fieldDiff.includeInvisible
        }
      else if (fieldDiff.alternatives && index - 1 < fieldDiff.alternatives.length) {
        return fieldDiff.alternatives[index - 1]
      } else {
        return {} // Old version didn't have this index?
      }
    },

    navigateToTab(index) {
      if (this.urlTabIndex !== index)
        this.$router.push({
          name: this.rotename,
          params: { appId: this.appId, fieldId: this.fieldId, tabIndex: index }
        })
    },

    shiftLocator(locator, byOffset) {
      const proposedIndex = this.currentTabIndex + byOffset
      if (proposedIndex >= 0 && proposedIndex < this.allLocators.length) {
        this.$set(this.allLocators, this.currentTabIndex, this.allLocators[proposedIndex])
        this.$set(this.allLocators, proposedIndex, locator)
        this.navigateToTab(proposedIndex)
      }
    },

    deleteLocator() {
      this.$set(this, 'allLocators', _.without(this.allLocators, this.currentLocator))
    },

    addLocator() {
      this.allLocators.push({
        type: 'path',
        path: '',
        image: '',
        offset: {},
        matchondesktop: false
      })
      this.navigateToTab(this.allLocators.length - 1)
    },

    ensureDebugSocketOpen() {
      this.flowRunning = true
      return this.getOpenDebugSocket().then(openSocket => {
        if (this.currentDebugSocket !== openSocket) {
          this.currentDebugSocket = openSocket
          openSocket.on('finished', this.flowFinishedHandler.bind(this))
          //NOTE: Doesn't require explicit cleanup
          openSocket.on('message', this.flowErrorMessageHandler.bind(this))
          openSocket.on('close', () => (this.currentDebugSocket = null))
        }
        return openSocket
      })
    },

    runAction(actionCode) {
      this.waitingForSocket = true
      this.ensureDebugSocketOpen()
        .then(() => {
          this.waitingForSocket = false
          // json serialize the output from a (function() { return (..); })() construct
          const flowCode = `JSON.stringify((function(field) { return (${actionCode}); })(${this.fieldCreationCode}), null, 2);`
          this.flowStartMs = Date.now()
          this.runFlow({
            flow: {
              id: 'one-off-flow',
              code: flowCode,
              application: this.appId
            },
            inputs: [],
            noTracerContext: true
          })
          // Prevent output area from collapsing when a new test action is started
          this.outputHeight = this.$refs.outputArea.clientHeight
            ? this.$refs.outputArea.clientHeight + 'px'
            : 0
          this.testOutput = `Executing ${actionCode} ... `
        })
        .catch(e => {
          this.cancelAction()
          console.warn('Failed to run field action.', e.message)
        })
    },

    cancelAction() {
      this.flowStartMs = 0
      this.testOutput = ''
      this.outputHeight = 0
      this.waitingForSocket = false
    },

    flowErrorMessageHandler(message) {
      if (message.Error) {
        this.flowStartMs = 0
        this.testOutput += 'Error: ' + message.Error
      }
    },

    flowFinishedHandler(message) {
      if (!this.flowStartMs) return
      const msElapsed = Date.now() - this.flowStartMs
      this.flowStartMs = 0
      this.outputHeight = 0
      this.testOutput += `completed in ${msElapsed}ms\n\n`
      if (message && message.decision === 'decline') {
        this.testOutput += 'Manatee declined: ' + message.reason
      } else if (
        message &&
        message.result &&
        message.result.result &&
        message.result.result.Value
      ) {
        if (this.finishedCallback) this.finishedCallback(message)
        else this.testOutput += 'Response: ' + message.result.result.Value
      } else if (message && message.result && message.result.error && message.result.error.Value) {
        this.testOutput += message.result.error.Value
      }
      this.flowRunning = false
    },

    lowerCaseInitial(s): string {
      return _.first(s).toLowerCase() + _.tail(s).join('')
    },

    getTabHeaderText(index, locator) {
      const type = this.lowerCaseInitial(
        locator.type === 'path'
          ? this.$t('FIELD_PATH_IDENTIFICATION')
          : this.$t('FIELD_OPTICAL_IDENTIFICATION')
      )
      if (index === 0) {
        return this.$t('FIELD_PRIMARY', { type })
      }
      return this.$t('FIELD_ALTERNATIVE', { type, index })
    },

    toggleIncludeInvisible($event) {
      const val = this.currentLocator.includeInvisible
      // Transitions: null => true => false => null
      this.$set(
        this.currentLocator,
        'includeInvisible',
        val == null ? true : val === true ? false : null
      )
      $event.preventDefault()
    }
  },

  mounted() {
    if (this.fieldId) {
      this.editMode()
    } else {
      this.createMode()
    }
    this.$nextTick(() => {
      if (this.$refs.droptarget) {
        ;['drag', 'dragstart', 'dragend', 'dragover', 'dragenter', 'dragleave', 'drop'].forEach(
          evt => {
            this.$refs.droptarget.addEventListener(evt, e => {
              e.preventDefault()
              e.stopPropagation()
            })
          }
        )
        this.$refs.droptarget.addEventListener('drop', e => {
          if (
            e.dataTransfer.files.length === 1 &&
            e.dataTransfer.files[0].type.indexOf('image') >= 0 &&
            !this.field.isDiff
          ) {
            const file = e.dataTransfer.files[0]
            this.processFile(file)
          }
        })
      }
    })
  },

  beforeDestroy() {
    this.closeDebugSocket() // This releases event handlers pointing into this vue component
  },

  watch: {
    appId: function() {
      this.readApp()
    },
    '$route.params.fieldId': function(newFieldId) {
      this.$v.$reset()
      this.pathInfos = []
      if (newFieldId) {
        this.editMode()
      } else {
        this.createMode()
      }
    },
    allLocators: {
      handler(newVal, oldVal) {
        if (!this.field) return
        if (!newVal || newVal.length === 0) {
          this.$set(this.field, 'alternatives', [])
        } else {
          // Reflect changes into field and field.alternatives
          const f = this.field
          const firstLocator = _.first(newVal)
          // Do not set the field path if it is an image locator
          f.path = firstLocator.type === 'image' ? '' : firstLocator.path
          f.offset = _.cloneDeep(firstLocator.offset)
          f.pathInfos = this.pathInfos || []
          if (firstLocator.matchconfidence != null) f.matchconfidence = firstLocator.matchconfidence
          if (firstLocator.matchondesktop != null) f.matchondesktop = firstLocator.matchondesktop
          f.image = firstLocator.image
          f.type = firstLocator.type
          if (firstLocator.traversalArgs) f.traversalArgs = firstLocator.traversalArgs
          if (firstLocator.includeInvisible != null)
            f.includeInvisible = firstLocator.includeInvisible
          // NOTE: This can be optimized - eg only clone the parts that have changed - or perhaps debounce the operation, so it doesn't happen for each keypress
          this.$set(f, 'alternatives', _.cloneDeep(_.tail(newVal)))
        }
      },
      deep: true
    },
    currentTabIndex(newVal, oldVal) {
      // Clear test output and abort any pending test
      this.cancelAction()
    }
  }
}
</script>
